import React from 'react'
import { useTranslation } from 'react-i18next'
import { IActiveTestSession } from '@/api/user/getActiveTestSessions.ts'
import TestSessionRow from '@/component/testMode/TestSessionRow.tsx'
import useTestSessionActions from '@/component/testMode/useTestSessionActions.tsx'
import { formatTestClock, splitByOwner } from '@/util/testSession.ts'

interface IProps {
    sessions: IActiveTestSession[]
    canEdit: boolean
}

const TestStatusPanel: React.FC<IProps> = ({ sessions, canEdit }) => {
    const { t } = useTranslation()
    const { end, endAll, isBusy } = useTestSessionActions()
    const { mine, others } = splitByOwner(sessions)

    const groups = [
        { id: 'mine', label: t('testMode.panel.mineGroup', { count: mine.length }), items: mine },
        { id: 'others', label: t('testMode.panel.othersGroup', { count: others.length }), items: others }
    ].filter(group => group.items.length > 0)

    const metaOf = (session: IActiveTestSession) =>
        session.isMine
            ? t('testMode.panel.mineMeta', { time: formatTestClock(session.createdAt) })
            : t('testMode.panel.otherMeta', { owner: session.createdByName, time: formatTestClock(session.createdAt) })

    return (
        <section className={'test-status-panel'}>
            <header className={'test-status-panel__head'}>
                <strong>{t('testMode.panel.title')}</strong>
                <span>{t('testMode.panel.summary', { mine: mine.length, others: others.length })}</span>
            </header>
            <div className={'test-status-panel__body app-scrollbar'}>
                {groups.map(group => (
                    <div key={group.id} className={'test-status-panel__group'}>
                        <p className={'test-status-panel__group-label'}>{group.label}</p>
                        <ul>
                            {group.items.map(session => (
                                <TestSessionRow
                                    key={session.id}
                                    name={session.protecteeName}
                                    serial={session.deviceSerial}
                                    meta={metaOf(session)}
                                    endsAt={session.endsAt}
                                    canEnd={canEdit}
                                    isBusy={isBusy(session.protecteeId)}
                                    onEnd={() => end(session)}
                                />
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <footer className={'test-status-panel__footer'}>
                <span>{t('testMode.panel.footer')}</span>
                {canEdit && mine.length > 0 && (
                    <button
                        type={'button'}
                        className={'test-mode-button'}
                        disabled={mine.some(session => isBusy(session.protecteeId))}
                        onClick={() => endAll(mine)}>
                        {t('testMode.panel.endAllMine')}
                    </button>
                )}
            </footer>
        </section>
    )
}

export default TestStatusPanel
